import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ClientLogEntry, ClientLogLevel } from '../models/client-log-entry';
import { ConfigService } from './config.service';
import { BYPASS_CLIENT_LOGGING, BYPASS_LOADER } from './logging-context';

@Injectable({
  providedIn: 'root'
})
export class LoggerService {
  private baseUrl = '';
  private environment = '';

  constructor(
    private readonly http: HttpClient,
    private readonly configService: ConfigService
  ) {
    this.configService.$configuration.subscribe(config => {
      this.baseUrl = config?.servicesUrl ?? '';
      this.environment = (config?.environment ?? '').toUpperCase();
    });
  }

  public debug(category: string, message: string, properties?: Record<string, unknown>): void {
    this.write('Debug', category, message, properties);
  }

  public info(category: string, message: string, properties?: Record<string, unknown>): void {
    this.write('Information', category, message, properties);
  }

  public warn(category: string, message: string, properties?: Record<string, unknown>, error?: unknown): void {
    this.write('Warning', category, message, properties, error);
  }

  public error(category: string, message: string, error?: unknown, properties?: Record<string, unknown>): void {
    this.write('Error', category, message, properties, error);
  }

  public httpError(category: string, requestUrl: string, error: unknown, properties?: Record<string, unknown>): void {
    const endpointPath = this.sanitizePath(requestUrl);
    const statusCode = error instanceof HttpErrorResponse ? error.status : undefined;
    const traceId = error instanceof HttpErrorResponse ? this.readTraceId(error) : undefined;

    this.write('Error', category, 'HTTP request failed', {
      ...properties,
      endpointPath,
      statusCode,
      traceId
    }, error);
  }

  private write(
    level: ClientLogLevel,
    category: string,
    message: string,
    properties?: Record<string, unknown>,
    error?: unknown
  ): void {
    if (!this.baseUrl) {
      return;
    }

    if (level === 'Debug' && this.environment === 'PROD') {
      return;
    }

    const entry = this.buildEntry(level, category, message, properties, error);
    const context = new HttpContext()
      .set(BYPASS_CLIENT_LOGGING, true)
      .set(BYPASS_LOADER, true);

    void firstValueFrom(this.http.post<void>(`${this.baseUrl}/api/client-logs`, entry, { context }))
      .catch(() => undefined);
  }

  private buildEntry(
    level: ClientLogLevel,
    category: string,
    message: string,
    properties?: Record<string, unknown>,
    error?: unknown
  ): ClientLogEntry {
    const normalizedProperties = this.normalizeProperties(properties);

    return {
      level,
      category: this.sanitizeText(category, 100),
      message: this.sanitizeText(message, 500),
      route: this.sanitizePath(window.location.pathname),
      endpointPath: normalizedProperties.endpointPath,
      operation: normalizedProperties.operation,
      statusCode: this.parseStatusCode(normalizedProperties.statusCode),
      traceId: normalizedProperties.traceId,
      errorType: this.resolveErrorType(error),
      properties: normalizedProperties.extra
    };
  }

  private normalizeProperties(properties?: Record<string, unknown>): {
    endpointPath?: string;
    operation?: string;
    statusCode?: string;
    traceId?: string;
    extra?: Record<string, string>;
  } {
    if (!properties) {
      return {};
    }

    const extra: Record<string, string> = {};
    let endpointPath: string | undefined;
    let operation: string | undefined;
    let statusCode: string | undefined;
    let traceId: string | undefined;

    for (const [key, value] of Object.entries(properties)) {
      if (value === undefined || value === null) {
        continue;
      }

      const sanitizedValue = this.sanitizeText(String(value), 250);
      switch (key) {
        case 'endpointPath':
          endpointPath = this.sanitizePath(String(value));
          break;
        case 'operation':
          operation = sanitizedValue;
          break;
        case 'statusCode':
          statusCode = sanitizedValue;
          break;
        case 'traceId':
          traceId = sanitizedValue;
          break;
        default:
          extra[this.sanitizeText(key, 60)] = sanitizedValue;
          break;
      }
    }

    return {
      endpointPath,
      operation,
      statusCode,
      traceId,
      extra: Object.keys(extra).length > 0 ? extra : undefined
    };
  }

  private resolveErrorType(error?: unknown): string | undefined {
    if (!error) {
      return undefined;
    }

    if (error instanceof HttpErrorResponse) {
      return 'HttpErrorResponse';
    }

    if (error instanceof Error) {
      return this.sanitizeText(error.name, 80);
    }

    return this.sanitizeText(typeof error, 80);
  }

  private readTraceId(error: HttpErrorResponse): string | undefined {
    const responseTraceId = error.error?.traceId;
    if (typeof responseTraceId === 'string' && responseTraceId.length > 0) {
      return this.sanitizeText(responseTraceId, 100);
    }

    const headerTraceId = error.headers.get('x-trace-id') ?? error.headers.get('request-id');
    return headerTraceId ? this.sanitizeText(headerTraceId, 100) : undefined;
  }

  private parseStatusCode(value?: string): number | undefined {
    if (!value) {
      return undefined;
    }

    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private sanitizePath(value?: string): string | undefined {
    if (!value) {
      return undefined;
    }

    try {
      const url = new URL(value, window.location.origin);
      return this.sanitizeText(url.pathname, 200);
    } catch {
      return this.sanitizeText(value.split('?')[0], 200);
    }
  }

  private sanitizeText(value: string, maxLength: number): string {
    return value.replace(/[\r\n]+/g, ' ').trim().slice(0, maxLength);
  }
}